import { forwardRef, type InputHTMLAttributes, createContext, useContext } from 'react';
import { cn } from '../../utils/cn';
import { getFocusClasses } from '../../utils/accessibility';
import type { BaseComponentProps } from '../../types';

/**
 * Context per RadioGroup
 */
interface RadioGroupContextValue {
  name: string;
  value?: string;
  onChange?: (value: string) => void;
}

const RadioGroupContext = createContext<RadioGroupContextValue | undefined>(undefined);

/**
 * RadioGroup Props
 */
export interface RadioGroupProps extends BaseComponentProps {
  /**
   * Name del gruppo (condiviso da tutti i radio)
   */
  name: string;
  /**
   * Valore selezionato
   */
  value?: string;
  /**
   * Callback quando cambia la selezione
   */
  onChange?: (value: string) => void;
  /**
   * Label del gruppo
   */
  label?: string;
  /**
   * Testo di aiuto
   */
  helpText?: string;
  /**
   * Stato di errore
   */
  error?: boolean;
  /**
   * Messaggio di errore
   */
  errorMessage?: string;
}

/**
 * RadioGroup Component
 * 
 * Container per raggruppare Radio buttons con gestione dello stato.
 * 
 * @example
 * ```tsx
 * <RadioGroup name="size" value={size} onChange={setSize} label="Taglia">
 *   <Radio value="s" label="Small" />
 *   <Radio value="m" label="Medium" />
 *   <Radio value="l" label="Large" />
 * </RadioGroup>
 * ```
 */
export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      className,
      name,
      value,
      onChange,
      label,
      helpText,
      error,
      errorMessage,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <RadioGroupContext.Provider value={{ name, value, onChange }}>
        <div ref={ref} className={cn('', className)} {...props}>
          {label && (
            <label className="block text-sm font-medium text-base-content mb-2">
              {label}
            </label>
          )}
          
          <div className="space-y-2">
            {children}
          </div>
          
          {error && errorMessage && (
            <p className="mt-1 text-sm text-error" role="alert">
              {errorMessage}
            </p>
          )}
          
          {!error && helpText && (
            <p className="mt-1 text-sm text-base-content/60">
              {helpText}
            </p>
          )}
        </div>
      </RadioGroupContext.Provider>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';

/**
 * Radio Props
 */
export interface RadioProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'size'>,
    BaseComponentProps {
  /**
   * Label del radio
   */
  label?: string;
  /**
   * Descrizione
   */
  description?: string;
  /**
   * Dimensione
   */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Radio Component
 * 
 * Single radio button, usato dentro RadioGroup.
 * 
 * @example
 * ```tsx
 * <Radio value="option1" label="Opzione 1" description="Descrizione opzione" />
 * ```
 */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      className,
      label,
      description,
      size = 'md',
      value,
      id,
      ...props
    },
    ref
  ) => {
    const context = useContext(RadioGroupContext);
    
    const sizeClasses = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
    };

    const radioId = id || `radio-${value || Math.random().toString(36).substr(2, 9)}`;

    const radioClasses = cn(
      'border-base-300 text-primary',
      'focus:ring-primary focus:ring-offset-0',
      'transition-colors cursor-pointer',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      sizeClasses[size],
      getFocusClasses(),
      className
    );

    const isChecked = context?.value === value;

    return (
      <div className="flex items-start">
        <div className="flex items-center h-5">
          <input
            ref={ref}
            id={radioId}
            type="radio"
            name={context?.name}
            value={value}
            checked={isChecked}
            onChange={(e) => {
              if (context?.onChange && e.target.value) {
                context.onChange(e.target.value);
              }
            }}
            className={radioClasses}
            {...props}
          />
        </div>
        
        {(label || description) && (
          <div className="ml-3">
            {label && (
              <label
                htmlFor={radioId}
                className="text-sm font-medium text-base-content cursor-pointer"
              >
                {label}
              </label>
            )}
            {description && (
              <p className="text-sm text-base-content/60">
                {description}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Radio.displayName = 'Radio';

